import { RewardType } from '~/shared';

export const REWARD_TYPE_LABELS: Record<RewardType, string> = {
	[RewardType.MONEY]: 'Деньги',
	[RewardType.EXP]: 'Опыт',
	[RewardType.ITEM]: 'Предмет',
	[RewardType.VEHICLE]: 'Транспорт',
	[RewardType.LOOTBOX]: 'Кейс',
	[RewardType.BUNDLE]: 'Набор',
	[RewardType.VEHICLE_TUNING_KIT]: 'Набор тюнинга',
	[RewardType.VEHICLE_TUNING_ITEM]: 'Элемент тюнинга',
};

export const REWARD_TYPE_OPTIONS = Object.values(RewardType).map((type) => ({
	value: type,
	label: REWARD_TYPE_LABELS[type],
}));

export const getRewardLabel = (type: RewardType): string => {
	switch (type) {
		case RewardType.MONEY:
			return 'Сумма (Руб.)';
		case RewardType.EXP:
			return 'Количество XP';
		case RewardType.ITEM:
			return 'ID предмета';
		case RewardType.VEHICLE:
			return 'Модель ТС';
		case RewardType.LOOTBOX:
			return 'ID кейса';
		case RewardType.BUNDLE:
			return 'ID набора';
		case RewardType.VEHICLE_TUNING_KIT:
			return 'ID набора тюнинга';
		case RewardType.VEHICLE_TUNING_ITEM:
			return 'ID элемента тюнинга';
		default:
			return '';
	}
};

export const getRewardPlaceholder = (type: RewardType): string => {
	switch (type) {
		case RewardType.MONEY:
			return '1 000 000';
		case RewardType.EXP:
			return '1 000';
		case RewardType.ITEM:
			return 'item_id';
		case RewardType.VEHICLE_TUNING_KIT:
			return 'kit_id';
		case RewardType.VEHICLE_TUNING_ITEM:
			return 'item_id';
		default:
			return '';
	}
};

export const getSelectPlaceholder = (type: RewardType): string => {
	switch (type) {
		case RewardType.VEHICLE:
			return 'Выберите транспорт...';
		case RewardType.LOOTBOX:
			return 'Выберите кейс...';
		case RewardType.BUNDLE:
			return 'Выберите набор...';
		case RewardType.ITEM:
			return 'Выберите предмет...';
		case RewardType.VEHICLE_TUNING_KIT:
			return 'Выберите кит тюнинга...';
		case RewardType.VEHICLE_TUNING_ITEM:
			return 'Выберите деталь тюнинга...';
		default:
			return '';
	}
};

export const getFilterPlaceholder = (type: RewardType): string => {
	switch (type) {
		case RewardType.VEHICLE:
			return 'Поиск транспорта...';
		case RewardType.LOOTBOX:
			return 'Поиск кейса...';
		case RewardType.BUNDLE:
			return 'Поиск набора...';
		case RewardType.ITEM:
			return 'Поиск предмета...';
		case RewardType.VEHICLE_TUNING_KIT:
			return 'Поиск кита тюнинга...';
		case RewardType.VEHICLE_TUNING_ITEM:
			return 'Поиск детали тюнинга...';
		default:
			return '';
	}
};

export const isSearchableType = (type: RewardType): boolean => {
	return (
		type === RewardType.VEHICLE ||
		type === RewardType.LOOTBOX ||
		type === RewardType.BUNDLE ||
		type === RewardType.ITEM ||
		type === RewardType.VEHICLE_TUNING_KIT ||
		type === RewardType.VEHICLE_TUNING_ITEM
	);
};

export const parseRewardValue = (value: string): Record<string, unknown> => {
	try {
		return JSON.parse(value || '{}') as Record<string, unknown>;
	} catch {
		return { value };
	}
};

export const formatRewardDisplay = (
	type: RewardType,
	value: string,
	formatNumber: (num: number | string) => string,
): string => {
	const parsed = parseRewardValue(value) as {
		id?: string;
		count?: number;
		model?: string;
		value?: string;
		tuning?: {
			items?: string[];
			kit?: string;
		};
	};
	switch (type) {
		case RewardType.MONEY:
			return value ? `${formatNumber(value)} руб.` : '...';
		case RewardType.EXP:
			return value ? `${formatNumber(value)} ед.` : '...';
		case RewardType.ITEM:
			return `ID: ${parsed.id || '-'}, кол-во: ${formatNumber(
				parsed.count || 1,
			)}`;
		case RewardType.VEHICLE: {
			const parts: string[] = [];
			parts.push(`Модель: ${parsed.model || '-'}`);
			if (parsed.tuning?.kit) {
				parts.push(`Кит: ${parsed.tuning.kit}`);
			}
			if (parsed.tuning?.items && parsed.tuning.items.length > 0) {
				parts.push(`Детали: ${parsed.tuning.items.join(', ')}`);
			}
			return parts.join(' | ');
		}
		case RewardType.LOOTBOX:
			return `ID: ${parsed.id || parsed.value || '-'}, кол-во: ${formatNumber(
				parsed.count || 1,
			)}`;
		case RewardType.BUNDLE:
			return `ID: ${parsed.value || '-'}`;
		case RewardType.VEHICLE_TUNING_KIT:
			return `ID кита: ${value || '-'}`;
		case RewardType.VEHICLE_TUNING_ITEM:
			return `ID детали: ${value || '-'}`;
		default:
			return value || '-';
	}
};

export const formatRewardDisplayList = (
	type: RewardType,
	value: string,
): string => {
	const formatNum = (v: string) => parseInt(v).toLocaleString('ru-RU');
	try {
		const parsed = JSON.parse(value);
		switch (type) {
			case RewardType.MONEY:
				return `${formatNum(value)} руб.`;
			case RewardType.EXP:
				return `${formatNum(value)} ед.`;
			case RewardType.ITEM:
				return `${parsed.id || '-'}, кол-во: ${formatNum(
					String(parsed.count || 1),
				)}`;
			case RewardType.VEHICLE: {
				const parts: string[] = [];
				parts.push(`Модель: ${parsed.model || '-'}`);
				if (parsed.tuning?.kit) {
					parts.push(`Кит: ${parsed.tuning.kit}`);
				}
				if (parsed.tuning?.items && parsed.tuning.items.length > 0) {
					parts.push(`Детали: ${parsed.tuning.items.join(', ')}`);
				}
				return parts.join(' | ');
			}
			case RewardType.LOOTBOX:
				return `${parsed.id || '-'}, кол-во: ${formatNum(
					String(parsed.count || 1),
				)}`;
			case RewardType.BUNDLE:
				return `ID: ${parsed.value || parsed.id || '-'}`;
			case RewardType.VEHICLE_TUNING_KIT:
				return `ID кита: ${value || '-'}`;
			case RewardType.VEHICLE_TUNING_ITEM:
				return `ID детали: ${value || '-'}`;
			default:
				return value || '-';
		}
	} catch {
		if (type === RewardType.MONEY || type === RewardType.EXP)
			return `${formatNum(value)} ${
				type === RewardType.MONEY ? 'руб.' : 'ед.'
			}`;
		return value || '-';
	}
};

export const formatDate = (timestamp: number | null): string => {
	if (!timestamp) return 'Не задана';
	return new Date(timestamp).toLocaleString('ru-RU', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});
};

export const getCollectionForType = (type: RewardType): 'items' | null => {
	switch (type) {
		case RewardType.LOOTBOX:
		case RewardType.BUNDLE:
		case RewardType.ITEM:
			return 'items';
		default:
			return null;
	}
};

export const getRewardId = (type: RewardType, value: string): string => {
	try {
		const parsed = JSON.parse(value);
		if (type === RewardType.ITEM || type === RewardType.LOOTBOX)
			return parsed.id || '';
		if (type === RewardType.VEHICLE) return parsed.model || '';
		if (type === RewardType.BUNDLE) return parsed.value || parsed.id || '';
		return parsed.value || '';
	} catch {
		return value;
	}
};
